/**
 * Navigation helpers shared by the customer screens.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * A back control that returns the customer to where they actually came from.
 *
 * The browser's own back button replays the session; an in-app back control
 * that always jumps to one fixed screen does not. Reaching My orders from the
 * menu and being sent to the landing page is the case that made this visible —
 * the landing page is not behind the customer at that moment, so the control
 * was answering a question nobody asked.
 *
 * React Router labels the first entry of a session `default`. Anything else was
 * reached by a navigation inside the app, so there is somewhere to go back to
 * and the history is the honest answer. The fallback covers the customer who
 * opened the link directly — a bookmark, a shared message, a typed code — where
 * stepping back would leave the app and land them on whatever page sent them
 * here, which is somewhere they never were.
 *
 * @param fallback Where to go when this screen is the first thing opened.
 * @returns A handler for a back control.
 */
export function useBackTo(fallback: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    if (location.key !== 'default') navigate(-1);
    else navigate(fallback);
  }, [navigate, location.key, fallback]);
}
